import { Box, Flex, Skeleton, Spinner, Text, useTheme } from '@chakra-ui/react';
import { QuickInsightItem } from './types';
import { useNavigate } from 'react-router-dom';
import { DownArrowIcon, UpArrowIcon } from '@devd-client/devd/components';

export const QuickInsightCard = ({
  summary,
  previousPeriodStat,
  isLoading,
  onClickCard,
  disableLink,
}: QuickInsightItem) => {
  const { colors } = useTheme();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box position="relative">
        <Skeleton height="140px" borderRadius="8px" />
        <Spinner position="absolute" top="42%" left="42%" />
      </Box>
    );
  }

  const percentChange = previousPeriodStat?.percentChange ?? 0;
  const improved = previousPeriodStat?.improved ?? false;
  const hasChange = percentChange !== 0;

  return (
    <Flex
      padding={5}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="12px"
      direction="column"
      justify="space-between"
      minHeight="140px"
      bg="white"
      boxShadow="0px 1px 3px 0px rgba(16, 24, 40, 0.06), 0px 1px 2px 0px rgba(16, 24, 40, 0.03)"
      transition="all 0.25s ease"
      cursor={summary?.link ? 'pointer' : 'default'}
      _hover={{
        boxShadow:
          '0px 4px 12px 0px rgba(16, 24, 40, 0.08), 0px 2px 4px 0px rgba(16, 24, 40, 0.04)',
        borderColor: 'gray.300',
        transform: 'translateY(-1px)',
      }}
      onClick={() => {
        if (onClickCard) {
          onClickCard();
          return;
        }

        if (!disableLink && summary?.link) {
          navigate(
            `${summary.link}${
              summary.link.includes('?')
                ? `&${window.location.href.split('?')[1]}`
                : `?${window.location.href.split('?')[1]}`
            }`
          );
        }
      }}
    >
      {/* Top: Title */}
      <Flex align="center">
        <Text
          fontFamily="heading"
          fontSize="sm"
          fontWeight="medium"
          color="text.secondary2"
          noOfLines={1}
        >
          {summary?.title || '-'}
        </Text>
      </Flex>

      {/* Middle: Count */}
      <Flex align="baseline" mt={3} gap={3}>
        <Text
          fontFamily="heading"
          fontSize="3xl"
          fontWeight="bold"
          color="text.primary"
          lineHeight="1"
        >
          {summary?.count ?? '-'}
        </Text>

        {/* Trend badge */}
        {hasChange && (
          <Flex
            align="center"
            gap={1}
            px={2}
            py={0.5}
            borderRadius="full"
            bg={improved ? 'rgba(29, 164, 74, 0.08)' : 'rgba(230, 81, 0, 0.08)'}
          >
            {percentChange > 0 ? (
              <UpArrowIcon color={improved ? colors.success : colors.error} />
            ) : (
              <DownArrowIcon color={improved ? colors.success : colors.error} />
            )}
            <Text
              fontFamily="heading"
              fontSize="xs"
              fontWeight="semibold"
              color={improved ? 'success' : 'error'}
            >
              {Math.abs(percentChange)}%
            </Text>
          </Flex>
        )}
        {!hasChange && typeof percentChange !== 'undefined' && (
          <Text
            fontFamily="heading"
            fontSize="xs"
            fontWeight="medium"
            color="text.secondary"
          >
            0% change
          </Text>
        )}
      </Flex>

      {/* Bottom: Previous period */}
      <Text
        mt={2}
        fontFamily="heading"
        fontSize="xs"
        fontWeight="normal"
        color="text.secondary"
        noOfLines={1}
      >
        {summary?.subTitle || ''}
      </Text>
    </Flex>
  );
};
